import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Typography from '@mui/material/Typography';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Assuming you have hooks for create and update
import { useCreateArticle, useUpdateArticle, useUploadImage, useUploadDocument, useListOrganizations, useListArticleFilters } from '../services/appService';
import { useAuth } from '../context/AuthContext';

const MAX_IMAGES = 10;

function ArticleFormModal({ open, onClose, onSave, article, appId }) {
  const { isOrgAdmin, getOrgIds } = useAuth();
  const [content, setContent] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    filter_name: '',
    is_published: true,
    organization_id: '',
    is_pinned: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEditMode = Boolean(article && article.id);
  const quillRef = useRef(null);

  // Organization list state
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const listOrganizationsHook = useListOrganizations();

  // Multi-image state
  // Each item: { id?: string (existing DB id), image_url: string, display_order: number, isUploading?: boolean, localPreview?: string }
  const [articleImages, setArticleImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]); // IDs of existing images to delete
  const imageFileInputRef = useRef(null);

  // Attachment state
  const [articleAttachments, setArticleAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
  const attachmentFileInputRef = useRef(null);

  // Filter state
  const [filters, setFilters] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const listFiltersHook = useListArticleFilters();

  const createArticleHook = useCreateArticle();
  const updateArticleHook = useUpdateArticle();
  const uploadImageHook = useUploadImage();
  const uploadDocumentHook = useUploadDocument();

  // Cleanup local preview URLs on unmount
  useEffect(() => {
    return () => {
      articleImages.forEach(img => {
        if (img.localPreview) URL.revokeObjectURL(img.localPreview);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset form when modal opens or article prop changes
  useEffect(() => {
    if (open) {
      // Reset common states
      setError('');
      setArticleImages([]);
      setImagesToDelete([]);
      setArticleAttachments([]);
      setAttachmentsToDelete([]);

      // Fetch Organizations
      setOrgLoading(true);
      listOrganizationsHook(appId)
        .then(orgData => setOrganizations(orgData || []))
        .catch(orgErr => {
            console.error("Failed to fetch organizations:", orgErr);
            setError(prev => prev ? `${prev}\nFailed to load organizations.` : 'Failed to load organizations.');
            setOrganizations([]);
        })
        .finally(() => setOrgLoading(false));

      // Fetch Filters
      setFilterLoading(true);
      listFiltersHook(appId)
        .then(filterData => {
            const fetchedFilters = filterData || [];
            setFilters(fetchedFilters);
            const defaultFilter = fetchedFilters.length > 0 ? fetchedFilters[0].name : '';

            if (article && article.id) {
                // --- EDIT MODE ---
                console.log(`[ArticleFormModal Edit] Received article:`, article);
                const initialIsPinned = article.is_pinned || false;
                const initialFilter = article.type || defaultFilter;

                setFormData({
                    title: article.title || '',
                    filter_name: initialFilter,
                    is_published: article.is_published === undefined ? true : article.is_published,
                    organization_id: article.organization_id || '',
                    is_pinned: initialIsPinned,
                });
                setContent(article.content || '');

                // Load existing images from article.images array
                if (Array.isArray(article.images) && article.images.length > 0) {
                    setArticleImages(article.images.map(img => ({
                        id: img.id,
                        image_url: img.image_url,
                        display_order: img.display_order,
                    })));
                } else if (article.image_url) {
                    // Fallback: use legacy image_url as single image
                    setArticleImages([{
                        image_url: article.image_url,
                        display_order: 0,
                    }]);
                }

                if (Array.isArray(article.attachments) && article.attachments.length > 0) {
                    setArticleAttachments(article.attachments.map(att => ({
                        id: att.id,
                        file_url: att.file_url,
                        file_name: att.file_name,
                        file_size: att.file_size,
                        mime_type: att.mime_type,
                        display_order: att.display_order,
                    })));
                }

            } else if (article && !article.id) {
                 // --- CREATE MODE with PRE-FILLED data (e.g., from Chat) ---
                 console.log(`[ArticleFormModal Create Pre-filled] Received data:`, article);
                 setFormData({
                     title: article.title || '',
                     filter_name: article.filter_name || defaultFilter,
                     is_published: article.is_published === undefined ? false : article.is_published,
                     organization_id: article.organization_id || '',
                     is_pinned: article.is_pinned || false,
                 });
                 setContent(article.content || '');

            } else {
                // --- CREATE MODE (Blank) ---
                 console.log(`[ArticleFormModal Create Blank] Initializing blank form.`);
                 const orgAdminOrgId = isOrgAdmin() && getOrgIds().length > 0 ? getOrgIds()[0] : '';
                 setFormData({
                    title: '',
                    filter_name: defaultFilter,
                    is_published: true,
                    organization_id: orgAdminOrgId,
                    is_pinned: false,
                });
                setContent('');
            }
        })
        .catch(filterErr => {
            console.error("Failed to fetch article filters:", filterErr);
            setError(prev => prev ? `${prev}\nFailed to load filters.` : 'Failed to load filters.');
            setFilters([]);
            if (!article) {
                 setFormData({
                    title: '', filter_name: '',
                    is_published: true, organization_id: '', is_pinned: false
                 });
                 setContent('');
             } else if (article && !article.id) {
                  setFormData({
                    title: article.title || '',
                    filter_name: article.filter_name || '',
                    is_published: article.is_published === undefined ? false : article.is_published,
                    organization_id: article.organization_id || '',
                    is_pinned: article.is_pinned || false,
                 });
                 setContent(article.content || '');
             }
        })
        .finally(() => setFilterLoading(false));

    }
  }, [open, article, appId, listOrganizationsHook, listFiltersHook]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'is_pinned_checkbox') {
        setFormData(prevData => ({
            ...prevData,
            is_pinned: checked,
        }));
    } else {
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }
  };

  const handleContentChange = (value) => {
    setContent(value);
  };

  const isAnyImageUploading = articleImages.some(img => img.isUploading);
  const isAnyAttachmentUploading = articleAttachments.some(att => att.isUploading);

  const handleClose = () => {
    if (!loading && !isAnyImageUploading && !isAnyAttachmentUploading) {
      onClose();
    }
  };

  // --- Multi-Image Handlers ---

  const handleAddImages = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setError('');

    const currentCount = articleImages.length;
    const slotsAvailable = MAX_IMAGES - currentCount;
    const filesToUpload = files.slice(0, slotsAvailable);

    if (files.length > slotsAvailable) {
      setError(`Maximum ${MAX_IMAGES} images allowed. Only ${slotsAvailable} image(s) added.`);
    }

    filesToUpload.forEach((file, index) => {
      const localPreview = URL.createObjectURL(file);
      const displayOrder = currentCount + index;
      const tempId = `temp_${Date.now()}_${index}`;

      // Add placeholder with uploading state
      setArticleImages(prev => [...prev, {
        _tempId: tempId,
        image_url: '',
        display_order: displayOrder,
        isUploading: true,
        localPreview,
      }]);

      // Upload the file
      uploadImageHook(appId, file)
        .then(uploadResult => {
          setArticleImages(prev => prev.map(img =>
            img._tempId === tempId
              ? { ...img, image_url: uploadResult.url, isUploading: false }
              : img
          ));
        })
        .catch(uploadError => {
          console.error(`Image upload error:`, uploadError);
          setError(prev => prev ? `${prev}\nFailed to upload image: ${uploadError.message}` : `Failed to upload image: ${uploadError.message}`);
          // Remove the failed image from the list
          setArticleImages(prev => prev.filter(img => img._tempId !== tempId));
          if (localPreview) URL.revokeObjectURL(localPreview);
        });
    });

    // Reset file input
    event.target.value = null;
  };

  const handleRemoveImage = (index) => {
    setArticleImages(prev => {
      const removed = prev[index];
      // If it's an existing DB image, track it for deletion
      if (removed.id) {
        setImagesToDelete(ids => [...ids, removed.id]);
      }
      // Revoke local preview URL
      if (removed.localPreview) URL.revokeObjectURL(removed.localPreview);
      // Remove and re-index display_order
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((img, i) => ({ ...img, display_order: i }));
    });
  };

  // --- Attachment Handlers ---
  const handleAddAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setError('');

    files.forEach((file, index) => {
      const displayOrder = articleAttachments.length + index;
      const tempId = `attachment_${Date.now()}_${index}`;

      setArticleAttachments(prev => [...prev, {
        _tempId: tempId,
        file_url: '',
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || 'application/octet-stream',
        display_order: displayOrder,
        isUploading: true,
      }]);

      uploadDocumentHook(appId, file)
        .then(uploadResult => {
          setArticleAttachments(prev => prev.map(att =>
            att._tempId === tempId
              ? {
                  ...att,
                  file_url: uploadResult.url,
                  file_name: uploadResult.name || file.name,
                  file_size: uploadResult.size ?? file.size,
                  mime_type: uploadResult.mimeType || file.type || 'application/octet-stream',
                  isUploading: false,
                }
              : att
          ));
        })
        .catch(uploadError => {
          console.error('Attachment upload error:', uploadError);
          setError(prev => prev ? `${prev}\nFailed to upload file: ${uploadError.message}` : `Failed to upload file: ${uploadError.message}`);
          setArticleAttachments(prev => prev.filter(att => att._tempId !== tempId));
        });
    });

    event.target.value = null;
  };

  const handleRemoveAttachment = (index) => {
    setArticleAttachments(prev => {
      const removed = prev[index];
      if (removed?.id) {
        setAttachmentsToDelete(ids => [...ids, removed.id]);
      }
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((att, i) => ({ ...att, display_order: i }));
    });
  };

  const formatFileSize = (size) => {
    if (!size && size !== 0) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  // --- Quill Image Handler ---
  const quillImageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files ? input.files[0] : null;
      if (file && quillRef.current) {
        setLoading(true);
        setError('');
        try {
          const uploadResult = await uploadImageHook(appId, file);
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection(true);
          const imageUrl = uploadResult.url;
          console.log("Inserting image into Quill:", imageUrl);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
        } catch (uploadError) {
          console.error("Quill image upload error:", uploadError);
          setError(`Quill image upload failed: ${uploadError.message}`);
        } finally {
          setLoading(false);
        }
      }
    };
  }, [appId, uploadImageHook]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: quillImageHandler
      }
    },
     clipboard: {
        matchVisual: false,
    }
  }), [quillImageHandler]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isAnyImageUploading || isAnyAttachmentUploading) {
        setError("Please wait for uploads to complete.");
        return;
    }

    setLoading(true);
    setError('');

    if (!formData.title || !content || !formData.filter_name) {
        setError('Title, Content, and Filter/Category are required fields.');
        setLoading(false);
        return;
    }

    const newImages = articleImages.filter(img => !img.id && img.image_url);
    const newAttachments = articleAttachments.filter(att => !att.id && att.file_url);

    // Build the images payload for the API
    const allImagesForPayload = articleImages
      .filter(img => img.image_url) // Only include successfully uploaded ones
      .map((img, index) => ({
        image_url: img.image_url,
        display_order: index,
      }));

    const allAttachmentsForPayload = articleAttachments
      .filter(att => att.file_url)
      .map((att, index) => ({
        file_url: att.file_url,
        file_name: att.file_name,
        file_size: att.file_size || 0,
        mime_type: att.mime_type || 'application/octet-stream',
        display_order: index,
      }));

    const articleData = {
      title: formData.title,
      content: content,
      type: formData.filter_name,
      is_published: formData.is_published,
      organization_id: formData.organization_id || null,
      is_pinned: formData.is_pinned,
      pin_filter_name: formData.is_pinned ? formData.filter_name : null,
    };

    console.log("Submitting Article Data:", articleData);

    try {
      if (isEditMode) {
        // For update: send only new images and images to delete
        const updatePayload = {
          ...articleData,
          images: newImages.map((img) => ({
            image_url: img.image_url,
            // Compute display_order based on position in the full array
            display_order: articleImages.findIndex(ai => ai === img),
          })),
          attachments: newAttachments.map((att) => ({
            file_url: att.file_url,
            file_name: att.file_name,
            file_size: att.file_size || 0,
            mime_type: att.mime_type || 'application/octet-stream',
            display_order: articleAttachments.findIndex(existingAtt => existingAtt === att),
          })),
          images_to_delete: imagesToDelete,
          attachments_to_delete: attachmentsToDelete,
        };
        await updateArticleHook(appId, article.id, updatePayload);
      } else {
        // For create: send all images
        const createPayload = {
          ...articleData,
          images: allImagesForPayload,
          attachments: allAttachmentsForPayload,
        };
        await createArticleHook(appId, createPayload);
      }
      onSave();
      handleClose();
    } catch (err) {
      console.error("Error saving article:", err);
      setError(err.message || 'Failed to save article. Please check details.');
    } finally {
       setLoading(false);
    }
  };

  const isDataLoading = loading || orgLoading || filterLoading || isAnyImageUploading || isAnyAttachmentUploading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{isEditMode ? 'Edit Article' : 'Create New Article'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {/* Title, Filter, Published Switch */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                required
                fullWidth
                id="title"
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={isDataLoading}
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
               <FormControl fullWidth margin="dense" required disabled={isDataLoading || filters.length === 0}>
                    <InputLabel id="filter-select-label">Filter/Category</InputLabel>
                    <Select
                        labelId="filter-select-label"
                        id="filter_name"
                        name="filter_name"
                        value={formData.filter_name}
                        label="Filter/Category"
                        onChange={handleChange}
                    >
                        {filters.length === 0 && <MenuItem value="" disabled>Loading filters...</MenuItem>}
                        {filters.map((filt) => (
                            <MenuItem key={filt.id || filt.name} value={filt.name}>
                                {filt.name}
                            </MenuItem>
                        ))}
                    </Select>
                 </FormControl>
             </Grid>
            <Grid item xs={12} sm={6}>
                 {isOrgAdmin() ? (
                    // Org admin: show read-only organization name
                    <TextField
                        margin="dense"
                        fullWidth
                        label="Organization"
                        value={organizations.find(o => o.id === formData.organization_id)?.name || 'Loading...'}
                        disabled
                    />
                 ) : (
                    <FormControl fullWidth margin="dense" disabled={isDataLoading || orgLoading}>
                        <InputLabel id="organization-select-label">Organization (optional)</InputLabel>
                        <Select
                            labelId="organization-select-label"
                            id="organization_id"
                            name="organization_id"
                            value={formData.organization_id}
                            label="Organization (optional)"
                            onChange={handleChange}
                        >
                            <MenuItem value="">
                                <em>None (Redaktion)</em>
                            </MenuItem>
                            {organizations.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                    {org.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                 )}
             </Grid>
            <Grid item xs={12} sm={6}>
                 <FormControlLabel
                    control={<Switch checked={formData.is_published} onChange={handleChange} name="is_published" disabled={isDataLoading} />}
                    label="Published"
                    sx={{ mt: { xs: 0, sm: 2 } }}
                />
             </Grid>
            <Grid item xs={12} sm={6}>
                 <FormControlLabel
                    control={<Switch checked={formData.is_pinned} onChange={handleChange} name="is_pinned_checkbox" disabled={isDataLoading || !formData.filter_name} />}
                    label="Pin Article"
                />
             </Grid>

            {/* Multi-Image Upload Section */}
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                    Images ({articleImages.length}/{MAX_IMAGES})
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    border: '1px dashed grey',
                    borderRadius: 1,
                    p: 1.5,
                    minHeight: 100,
                    alignItems: 'flex-start',
                }}>
                    {articleImages.map((img, index) => (
                        <Box
                            key={img.id || img._tempId || index}
                            sx={{
                                position: 'relative',
                                width: 100,
                                height: 100,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: index === 0 ? 'primary.main' : 'grey.300',
                                flexShrink: 0,
                            }}
                        >
                            {img.isUploading ? (
                                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : (
                                <img
                                    src={img.localPreview || img.image_url}
                                    alt={`Artikel ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )}
                            {/* Cover badge for first image */}
                            {index === 0 && (
                                <Chip
                                    label="Cover"
                                    size="small"
                                    color="primary"
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        left: 2,
                                        height: 20,
                                        fontSize: '0.65rem',
                                    }}
                                />
                            )}
                            {/* Delete button */}
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveImage(index)}
                                disabled={isDataLoading}
                                sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    p: 0.3,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                }}
                            >
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    ))}

                    {/* Add Image Button */}
                    {articleImages.length < MAX_IMAGES && (
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isDataLoading}
                            sx={{
                                width: 100,
                                height: 100,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderStyle: 'dashed',
                                flexShrink: 0,
                            }}
                        >
                            <AddCircleOutlineIcon sx={{ mb: 0.5 }} />
                            <Typography variant="caption">Add</Typography>
                            <input
                                ref={imageFileInputRef}
                                type="file"
                                hidden
                                accept="image/*"
                                multiple
                                onChange={handleAddImages}
                            />
                        </Button>
                    )}
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    First image is used as cover. Max {MAX_IMAGES} images.
                </Typography>
            </Grid>

            {/* Attachment Upload Section */}
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                    Dokumente ({articleAttachments.length})
                </Typography>
                <Box sx={{
                    border: '1px dashed grey',
                    borderRadius: 1,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isDataLoading}
                            startIcon={<AddCircleOutlineIcon />}
                        >
                            Dokument hochladen
                            <input
                                ref={attachmentFileInputRef}
                                type="file"
                                hidden
                                multiple
                                onChange={handleAddAttachments}
                            />
                        </Button>
                    </Box>

                    {articleAttachments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Noch keine Dokumente angehängt.
                        </Typography>
                    ) : (
                        articleAttachments.map((att, index) => (
                            <Box
                                key={att.id || att._tempId || index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.25,
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {att.file_name || `Dokument ${index + 1}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {att.mime_type || 'Datei'}{att.file_size ? ` · ${formatFileSize(att.file_size)}` : ''}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                    {att.isUploading ? (
                                        <CircularProgress size={20} />
                                    ) : att.file_url ? (
                                        <Button size="small" href={att.file_url} target="_blank" rel="noopener noreferrer">
                                            Öffnen
                                        </Button>
                                    ) : null}
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveAttachment(index)}
                                        disabled={isDataLoading}
                                    >
                                        <ClearIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Unterstützt beliebige Dateitypen bis 10 MB pro Datei.
                </Typography>
            </Grid>

            {/* ReactQuill Editor */}
            <Grid item xs={12}>
                 <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Content</Typography>
                 <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: '4px', minHeight: 300 }}>
                    <ReactQuill
                        key={article?.id || 'new-article'}
                        ref={quillRef}
                        theme="snow"
                        value={content}
                        onChange={handleContentChange}
                        modules={modules}
                        formats={formats}
                        readOnly={isDataLoading}
                        placeholder="Write your article content here..."
                        style={{ height: 'calc(100% - 42px)', overflowY: 'auto' }}
                    />
                 </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3}}>
        <Button onClick={handleClose} disabled={isDataLoading}>Cancel</Button>
        <Button type="submit" variant="contained" onClick={handleSubmit} disabled={isDataLoading}>
          {isDataLoading && !isAnyImageUploading ? <CircularProgress size={24} /> : (isEditMode ? 'Save Changes' : 'Create Article')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ArticleFormModal;
